import { CLIENT_TOOL_GROUPS, CLIENT_TOOLS, normaliseHiddenTools } from '@cc/schema';
import { useCustomMutation, useTranslate, useWarnAboutChange } from '@refinedev/core';
import * as Sentry from '@sentry/react';
import { App as AntdApp, Button, Card, Divider, Flex, Space, Switch, Tag, Typography } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { GROUP_LABEL_KEYS, toolLabel } from '../../components/clientTools/labels';
import { useMyHiddenTools } from '../../hooks/useMyHiddenTools';
import { graphqlErrorInfo } from '../../utils/graphqlErrors';

/**
 * E57 · Settings › My tools (cc-practice, "Client Management — My tools (portal, E57)", screens e57e0003-…-001 / -003;
 * founder Q-pia-5b C): every tool of the portal in its group with a switch, all ON until the therapist switches one OFF for
 * herself. My tools decides which pills exist in her client-page row and their order (one row, the same for every client);
 * a hidden tool leaves that row and the pickers — except for a client who uses it, where it shows "hidden by you". Its
 * records stay under Caseload › All records. Another therapist keeps her own set. A tool added by a later release is not
 * in anyone's hidden set, so it appears ON.
 *
 * Saving is a deliberate act (the portal's no-autosave rule): the switches change a draft (and arm the leave-warning);
 * Save writes the whole hidden set (SetHiddenTools); Show everything saves an empty set.
 *
 * Behavior lines implemented here:
 *   · empty-state: nothing hidden → every switch on, "Show everything" disabled (myTools.nothingHidden);
 *   · help: a line under the title says a hidden tool never deletes anything (myTools.help);
 *   · nav: Settings › My tools in the global menu (myTools.menu);
 *   · format: groups as on the client page (clientPage.group.*);
 *   · gesture: Save → SetHiddenTools (myTools.save); Show everything → an empty set (myTools.showAll);
 *   · error-display: a refused save keeps the switches as they were (myTools.saveFailed).
 */
export const MyToolsPage: React.FC = () => {
  const t = useTranslate();
  const { message } = AntdApp.useApp();
  const { hiddenTools, isLoading, refetch } = useMyHiddenTools();
  const { setWarnWhen } = useWarnAboutChange();
  const { mutateAsync, isLoading: saving } = useCustomMutation<{ performer: string; hiddenTools: string[] }>();

  const [draft, setDraft] = useState<string[] | null>(null);
  const saved = useMemo(() => normaliseHiddenTools(hiddenTools), [hiddenTools]);
  const current = draft ?? saved;
  const dirty = draft !== null && draft.join(',') !== saved.join(',');

  useEffect(() => {
    setWarnWhen(dirty);
    return () => setWarnWhen(false);
  }, [dirty, setWarnWhen]);

  const toggle = (key: string, on: boolean) => {
    const next = new Set(current);
    if (on) next.delete(key);
    else next.add(key);
    setDraft(normaliseHiddenTools(Array.from(next)));
  };

  const save = async (set: string[]) => {
    try {
      const res = await mutateAsync({
        url: '',
        method: 'post',
        values: {},
        errorNotification: false,
        successNotification: false,
        meta: {
          operation: 'setHiddenTools',
          fields: ['performer', 'hiddenTools'],
          variables: {
            input: { name: 'input', type: 'SetHiddenToolsInput!', value: { hiddenTools: set } },
          },
        },
      });
      if (!res?.data) {
        // a null answer is a refusal, never a save (portal rule)
        message.error(t('myTools.saveFailed'));
        return;
      }
      setDraft(null);
      setWarnWhen(false);
      await refetch();
      message.success(t('myTools.saved'), 1);
    } catch (error) {
      const { code, message: serverMessage } = graphqlErrorInfo(error);
      if (code && serverMessage) message.error(serverMessage);
      else {
        message.error(t('myTools.saveFailed'));
        Sentry.captureException(error);
      }
      // the switches stay as they were (the draft is kept)
    }
  };

  const hiddenSet = new Set(current);
  return (
    <Card data-testid="cc-my-tools">
      <Typography.Title level={3} style={{ marginTop: 0 }}>
        {t('myTools.title')}
      </Typography.Title>
      <Typography.Paragraph type="secondary" data-testid="cc-my-tools-intro">
        {t('myTools.intro')}
      </Typography.Paragraph>
      <Typography.Paragraph type="secondary" data-testid="cc-my-tools-help">
        {t('myTools.help')}
      </Typography.Paragraph>
      {!isLoading && current.length === 0 ? (
        <Typography.Paragraph data-testid="cc-my-tools-nothing-hidden">{t('myTools.nothingHidden')}</Typography.Paragraph>
      ) : null}
      {CLIENT_TOOL_GROUPS.map((g) => (
        <section key={g} data-testid={`cc-my-tools-group-${g}`}>
          <Divider orientation="left" plain>
            {t(GROUP_LABEL_KEYS[g])}
          </Divider>
          <Flex wrap="wrap" gap={16}>
            {CLIENT_TOOLS.filter((d) => d.group === g).map((d) => {
              const on = !hiddenSet.has(d.key);
              return (
                <Space key={d.key} style={{ minWidth: 260 }}>
                  <Switch
                    checked={on}
                    disabled={isLoading || saving}
                    onChange={(v) => toggle(d.key, v)}
                    data-testid={`cc-my-tools-switch-${d.key}`}
                    aria-label={toolLabel(t, d.key)}
                  />
                  <span>{toolLabel(t, d.key)}</span>
                  {on ? null : <Tag data-testid={`cc-my-tools-hidden-${d.key}`}>{t('myTools.hiddenForMe')}</Tag>}
                </Space>
              );
            })}
          </Flex>
        </section>
      ))}
      <Divider />
      <Space>
        <Button
          type="primary"
          disabled={!dirty}
          loading={saving}
          onClick={() => save(current)}
          data-testid="cc-my-tools-save"
        >
          {t('myTools.save')}
        </Button>
        <Button
          disabled={saving || (saved.length === 0 && current.length === 0)}
          onClick={() => save([])}
          data-testid="cc-my-tools-show-all"
        >
          {t('myTools.showAll')}
        </Button>
      </Space>
    </Card>
  );
};
